'use client';

import type { CityCode } from '@loci/city-editions';

import { useCityReturnUrl } from '@/hooks/useCityReturnUrl';
import { crossHostLogin } from '@/lib/cross-host-redirect';
import type { CrossHostStageOptions } from '@/lib/cross-host-urls';

/**
 * Sign-in href for the marketing-city chrome. Returns `providedSignInUrl`
 * when the caller supplies one; otherwise
 * `crossHostLogin(currentUrl, { force: true })` — `force` so stale cookies
 * cannot bounce returning users off the login form (FRE-784), `currentUrl`
 * so a successful sign-in returns them to the city page they were reading
 * instead of the app feed (WEB-898).
 *
 * Single source of truth for that fallback so `CityChrome`, `CityNavMenu`,
 * and `CityMenuSheetContent` — which each accept an optional `signInUrl`
 * override and fall back independently for standalone (test/Storybook)
 * usage — cannot drift from one another.
 */
export function useCitySignInHref(
  cityCode: CityCode,
  providedSignInUrl: string | undefined,
  stage?: CrossHostStageOptions,
): string {
  const currentUrl = useCityReturnUrl(cityCode, stage);
  return providedSignInUrl ?? crossHostLogin(currentUrl, { force: true, ...stage });
}
